import { booleanAttribute, Component, contentChild, input } from '@angular/core';
import { MibHeaderEndDirective } from './header-end.directive';

// The line underneath is an inset `mib-divider` shadow, not a border, so the bar stays 64px tall.
// Below the expanded size class, its gap and gutters replace the base ones.
const HEADER_BAR =
  'flex h-16 w-full items-center gap-4 bg-white px-6 not-expanded:gap-1 not-expanded:px-4 ' +
  'shadow-[inset_0_-1px_0_0_var(--color-mib-divider)]';

/**
 * The application bar: 64px tall, a hairline underneath, three zones.
 *
 * It owns the structure and nothing else — the brand, the middle and the end are yours:
 *
 * ```html
 * <mib-header>
 *   <svg mib-logo-ai mibHeaderBrand></svg>
 *
 *   <span mibHeaderEnd>
 *     <button mibBareButton [mibMenuTrigger]="account" aria-label="Account of Dr. Martin">
 *       <mib-avatar username="Dr. Martin" size="sm" mode="subtle" />
 *     </button>
 *     <mib-menu #account align="end" [menuItems]="items">
 *       <mib-avatar mibMenuHeader class="w-full" size="md" mode="subtle"
 *         username="Dr. Martin" description="Cardiologist" withName />
 *     </mib-menu>
 *   </span>
 * </mib-header>
 * ```
 *
 * The logo is projected rather than picked by an input: each mark is its own component so an
 * application ships only the one it uses, and an input would force the header to import all ten.
 * The middle is a bare slot — tabs on one screen, a zone group on another, nothing on a third.
 * The end is optional, and the vertical rule in front of it appears only when something is there;
 * `[endRule]="false"` leaves it out.
 *
 * **There is no user component.** The signed-in user is an avatar that opens a menu, and both already
 * know how to draw themselves: `[mibMenuTrigger]` opens a panel from any element, and `<mib-avatar>`
 * carries the name beside the round — after it, or before it with `nameFirst` for a block sitting at
 * the end of the bar.
 */
@Component({
  selector: 'mib-header',
  templateUrl: './header.component.html',
  host: {
    role: 'banner',
    class: HEADER_BAR,
  },
})
export class MibHeaderComponent {
  /** Draws the vertical rule before the end zone; `false` leaves it out, for a bar whose end holds a single action. */
  readonly endRule = input(true, { transform: booleanAttribute });
  /** Lets the middle content take the whole bar, hiding the brand, the rule and the end. */
  readonly middleFills = input(false, { transform: booleanAttribute });

  protected readonly end = contentChild(MibHeaderEndDirective);
}
